(() => {
  'use strict';

  /*
   * ZYVIRA CART MILESTONE CELEBRATION
   * ------------------------------------------------------------
   * Visual-only enhancement.
   * Does not change cart items, totals, discounts, gift eligibility,
   * checkout, shipping logic or inventory validation.
   */

  const ROOT = window.Shopify?.routes?.root || '/';
  const CART_URL = `${ROOT}cart.js`;

  const MILESTONES = Object.freeze([
    Object.freeze({
      rank: 1,
      key: 'shipping',
      threshold: 79900,
      eyebrow: 'Zyvira benefit unlocked',
      title: 'FREE Shipping Unlocked!',
      message:
        'Your Zyvira order now qualifies for free standard delivery.'
    }),

    Object.freeze({
      rank: 2,
      key: 'organizer',
      threshold: 149900,
      eyebrow: 'Zyvira reward unlocked',
      title: 'Free Travel Organizer Unlocked!',
      message:
        'Your Mini Jewellery Travel Organizer worth ₹349 is now reserved.'
    }),

    Object.freeze({
      rank: 3,
      key: 'surprise',
      threshold: 349900,
      eyebrow: 'Zyvira premium reward',
      title: 'Premium Surprise Gift Unlocked!',
      message:
        'You’ve unlocked both exclusive Zyvira gifts with your order.'
    })
  ]);

  const MUTATION_SELECTOR = [
    '.qty-bt',
    'delpro-btn',
    '.remove-product',
    'cart-remove-button',
    '[name="add"]',
    '.quick-add-btn',
    '.product-form__submit'
  ].join(',');

  const QUANTITY_SELECTOR =
    '.qty-box-input, [name="updates[]"]';

  const CART_OPEN_SELECTOR =
    '#ajax-cart-icon, a[href="/cart"], a[href$="/cart"]';

  let refreshTimer = 0;
  let requestSequence = 0;
  let hideTimer = 0;
  let observer = null;

  const memoryState = new Map();

  const toNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const getRank = (total) => {
    if (total >= MILESTONES[2].threshold) return 3;
    if (total >= MILESTONES[1].threshold) return 2;
    if (total >= MILESTONES[0].threshold) return 1;

    return 0;
  };

  const getMilestone = (rank) => {
    return (
      MILESTONES.find(
        (milestone) => milestone.rank === rank
      ) || null
    );
  };

  const storageKey = (cart) => {
    const token = String(
      cart?.token || 'current-cart'
    );

    return `zyvira-cart-celebration:${token}`;
  };

  const readState = (key) => {
    try {
      const value = sessionStorage.getItem(key);

      if (value) {
        const state = JSON.parse(value);

        const normalized = {
          rank: Math.max(
            0,
            Math.min(3, toNumber(state?.rank))
          ),
          total: Math.max(
            0,
            toNumber(state?.total)
          )
        };

        memoryState.set(key, normalized);
        return normalized;
      }
    } catch (error) {
      // Fall through to in-memory storage.
    }

    return memoryState.get(key) || null;
  };

  const writeState = (key, rank, total) => {
    const state = {
      rank,
      total,
      updatedAt: Date.now()
    };

    memoryState.set(key, state);

    try {
      sessionStorage.setItem(
        key,
        JSON.stringify(state)
      );
    } catch (error) {
      /*
       * In-memory storage still prevents repeats
       * during the current page view.
       */
    }
  };

  const iconMarkup = (key) => {
    if (key === 'shipping') {
      return `
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 6h11v10H3zM14 10h4l3 3v3h-7z"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linejoin="round"
          />

          <path
            d="M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM18 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"
            stroke="currentColor"
            stroke-width="1.8"
          />
        </svg>
      `;
    }

    if (key === 'organizer') {
      return `
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M4 10h16v10H4zM3 7h18v3H3zM12 7v13"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linejoin="round"
          />

          <path
            d="M7.5 7C5.2 7 4 5.9 4 4.7 4 3.6 4.9 3 5.9 3 8 3 10 5.2 12 7M16.5 7C18.8 7 20 5.9 20 4.7 20 3.6 19.1 3 18.1 3 16 3 14 5.2 12 7"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>
      `;
    }

    return `
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M12 2.8l1.35 4.45L17.8 8.6l-4.45 1.35L12 14.4l-1.35-4.45L6.2 8.6l4.45-1.35L12 2.8Z"
          stroke="currentColor"
          stroke-width="1.7"
          stroke-linejoin="round"
        />

        <path
          d="M18.2 13.4l.8 2.6 2.6.8-2.6.8-.8 2.6-.8-2.6-2.6-.8 2.6-.8.8-2.6ZM5.2 13.8l.65 2.1 2.1.65-2.1.65-.65 2.1-.65-2.1-2.1-.65 2.1-.65.65-2.1Z"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linejoin="round"
        />
      </svg>
    `;
  };

  const ensureLayer = () => {
    let layer = document.getElementById(
      'zyvira-cart-celebration'
    );

    if (layer) return layer;

    layer = document.createElement('div');

    layer.id = 'zyvira-cart-celebration';
    layer.className = 'zyvira-cart-celebration';
    layer.setAttribute('aria-hidden', 'true');

    layer.innerHTML = `
      <div
        class="zyvira-cart-celebration__confetti"
        aria-hidden="true"
      ></div>

      <div
        class="zyvira-cart-celebration__toast"
        role="status"
        aria-live="assertive"
        aria-atomic="true"
      >
        <span
          class="zyvira-cart-celebration__icon"
          data-celebration-icon
          aria-hidden="true"
        ></span>

        <span class="zyvira-cart-celebration__content">
          <span
            class="zyvira-cart-celebration__eyebrow"
            data-celebration-eyebrow
          ></span>

          <strong
            class="zyvira-cart-celebration__title"
            data-celebration-title
          ></strong>

          <span
            class="zyvira-cart-celebration__message"
            data-celebration-message
          ></span>
        </span>
      </div>
    `;

    document.body.appendChild(layer);

    return layer;
  };

  const createConfetti = (container) => {
    if (!container) return;

    container.replaceChildren();

    const colors = [
      '#570517',
      '#8a1834',
      '#b78a45',
      '#e4c789',
      '#f4dfe4',
      '#ffffff'
    ];

    const pieceCount = window
      .matchMedia('(max-width: 479px)')
      .matches
        ? 34
        : 48;

    for (
      let index = 0;
      index < pieceCount;
      index += 1
    ) {
      const piece =
        document.createElement('span');

      const left = Math.random() * 100;
      const delay = Math.random() * 0.45;
      const duration =
        1.45 + Math.random() * 0.85;
      const drift =
        -70 + Math.random() * 140;
      const rotation =
        260 + Math.random() * 520;
      const width =
        5 + Math.random() * 5;
      const height =
        8 + Math.random() * 8;

      piece.className =
        'zyvira-cart-celebration__piece';

      piece.style.setProperty(
        '--zyv-confetti-left',
        `${left}%`
      );

      piece.style.setProperty(
        '--zyv-confetti-delay',
        `${delay}s`
      );

      piece.style.setProperty(
        '--zyv-confetti-duration',
        `${duration}s`
      );

      piece.style.setProperty(
        '--zyv-confetti-drift',
        `${drift}px`
      );

      piece.style.setProperty(
        '--zyv-confetti-rotate',
        `${rotation}deg`
      );

      piece.style.setProperty(
        '--zyv-confetti-width',
        `${width}px`
      );

      piece.style.setProperty(
        '--zyv-confetti-height',
        `${height}px`
      );

      piece.style.setProperty(
        '--zyv-confetti-color',
        colors[index % colors.length]
      );

      if (index % 5 === 0) {
        piece.classList.add('is-circle');
      }

      container.appendChild(piece);
    }
  };

  const hideCelebration = () => {
    const layer = document.getElementById(
      'zyvira-cart-celebration'
    );

    if (!layer) return;

    layer.classList.remove('is-visible');
    layer.setAttribute('aria-hidden', 'true');
  };

  const showCelebration = (milestone) => {
    if (!milestone) return;

    const layer = ensureLayer();

    const reducedMotion = window
      .matchMedia(
        '(prefers-reduced-motion: reduce)'
      )
      .matches;

    window.clearTimeout(hideTimer);

    layer.classList.remove('is-visible');
    layer.dataset.milestone = milestone.key;

    const icon = layer.querySelector(
      '[data-celebration-icon]'
    );

    const eyebrow = layer.querySelector(
      '[data-celebration-eyebrow]'
    );

    const title = layer.querySelector(
      '[data-celebration-title]'
    );

    const message = layer.querySelector(
      '[data-celebration-message]'
    );

    const confetti = layer.querySelector(
      '.zyvira-cart-celebration__confetti'
    );

    if (icon) {
      icon.innerHTML =
        iconMarkup(milestone.key);
    }

    if (eyebrow) {
      eyebrow.textContent =
        milestone.eyebrow;
    }

    if (title) {
      title.textContent =
        milestone.title;
    }

    if (message) {
      message.textContent =
        milestone.message;
    }

    if (reducedMotion) {
      confetti?.replaceChildren();
    } else {
      createConfetti(confetti);
    }

    /*
     * Force animation restart when milestones
     * are crossed close together.
     */
    void layer.offsetWidth;

    layer.classList.add('is-visible');
    layer.setAttribute('aria-hidden', 'false');

    hideTimer = window.setTimeout(
      hideCelebration,
      reducedMotion ? 2300 : 3400
    );
  };

  const evaluateCart = (
    cart,
    allowCelebration = true
  ) => {
    const total = Math.max(
      0,
      toNumber(cart?.total_price)
    );

    const currentRank = getRank(total);
    const key = storageKey(cart);
    const previousState = readState(key);

    /*
     * First observation establishes the baseline.
     * It does not celebrate on reload or a new tab.
     */
    if (!previousState) {
      writeState(
        key,
        currentRank,
        total
      );

      return;
    }

    if (
      allowCelebration &&
      currentRank > previousState.rank
    ) {
      /*
       * Display only the highest newly reached
       * milestone when multiple tiers are crossed.
       */
      showCelebration(
        getMilestone(currentRank)
      );
    }

    /*
     * Downgrades are saved silently.
     * Crossing the threshold again will celebrate.
     */
    writeState(
      key,
      currentRank,
      total
    );
  };

  const fetchCart = async (
    allowCelebration = true
  ) => {
    const sequence = ++requestSequence;

    try {
      const response = await fetch(
        CART_URL,
        {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Cart request failed: ${response.status}`
        );
      }

      const cart = await response.json();

      if (sequence !== requestSequence) {
        return;
      }

      evaluateCart(
        cart,
        allowCelebration
      );
    } catch (error) {
      console.warn(
        '[Zyvira celebration] Unable to read cart state.',
        error
      );
    }
  };

  const scheduleRefresh = (
    delay = 120
  ) => {
    window.clearTimeout(refreshTimer);

    refreshTimer = window.setTimeout(
      () => fetchCart(true),
      delay
    );
  };

  const scheduleCartMutationRefreshes =
    () => {
      scheduleRefresh(120);

      window.setTimeout(
        () => fetchCart(true),
        650
      );

      window.setTimeout(
        () => fetchCart(true),
        1350
      );
    };

  const mutationIsRelevant = (
    mutation
  ) => {
    const target =
      mutation.target instanceof Element
        ? mutation.target
        : mutation.target?.parentElement;

    if (
      target?.closest?.(
        'ajax-cart.mini-cart, .cart-page'
      )
    ) {
      return true;
    }

    return [
      ...mutation.addedNodes,
      ...mutation.removedNodes
    ].some((node) => {
      if (!(node instanceof Element)) {
        return false;
      }

      return (
        node.matches?.(
          'ajax-cart.mini-cart, .cart-page, .js-contents, cart-pro, ajax-items, .shipping-bar, [data-zyvira-rewards-card]'
        ) ||
        Boolean(
          node.querySelector?.(
            'ajax-cart.mini-cart, .cart-page, .js-contents, cart-pro, ajax-items, .shipping-bar, [data-zyvira-rewards-card]'
          )
        )
      );
    });
  };

  const initialize = () => {
    /*
     * Record the current tier without animation.
     */
    void fetchCart(false);

    document.addEventListener(
      'click',
      (event) => {
        if (
          event.target.closest(
            MUTATION_SELECTOR
          )
        ) {
          scheduleCartMutationRefreshes();
          return;
        }

        if (
          event.target.closest(
            CART_OPEN_SELECTOR
          )
        ) {
          scheduleRefresh(180);
        }
      },
      true
    );

    document.addEventListener(
      'change',
      (event) => {
        if (
          event.target.matches?.(
            QUANTITY_SELECTOR
          )
        ) {
          scheduleCartMutationRefreshes();
        }
      },
      true
    );

    [
      'cart:updated',
      'cart:change',
      'cart:refresh',
      'ajaxProduct:added',
      'product:added'
    ].forEach((eventName) => {
      document.addEventListener(
        eventName,
        scheduleCartMutationRefreshes
      );
    });

    observer = new MutationObserver(
      (mutations) => {
        if (
          mutations.some(
            mutationIsRelevant
          )
        ) {
          scheduleRefresh(100);
        }
      }
    );

    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();